import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Mail, Instagram, Youtube, Copy, Check } from "lucide-react";

export default function Footer() {
  const [mailOpen, setMailOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapperRef = useRef(null);
  const EMAIL = "info@zafertufan.com";

  useEffect(() => {
    if (!mailOpen) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setMailOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setMailOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [mailOpen]);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };

  return (
    <footer className="border-t border-white/5 mt-24 py-12" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid gap-8 md:grid-cols-4">
        <div>
          <div className="font-display text-2xl font-bold">zafertufan<span className="text-cyan-400">.com</span></div>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            Analog-hibrit mix & mastering stüdyosu. Doğal, punchy ve radyoya hazır sonuçlar için.
          </p>
        </div>
        <div>
          <div className="mono-caps mb-3">Menü</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/hakkimda" className="hover:text-cyan-300">Hakkımda</Link></li>
            <li><Link to="/alet-edevat" className="hover:text-cyan-300">Stüdyo Ekipmanları</Link></li>
            <li><Link to="/enstrumanlar" className="hover:text-cyan-300">Enstrümanistler</Link></li>
            <li><Link to="/siparis" className="hover:text-cyan-300">Sipariş Ver</Link></li>
          </ul>
        </div>
        <div>
          <div className="mono-caps mb-3">Fiyatlar</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li>20 Kanala Kadar · <span className="text-white">8.000 ₺</span></li>
            <li>40 Kanala Kadar · <span className="text-white">12.000 ₺</span></li>
            <li>40+ Kanal · <span className="text-white">15.000 ₺</span></li>
            <li>Mastering · <span className="text-white">5.000 ₺</span></li>
          </ul>
        </div>
        <div>
          <div className="mono-caps mb-3">İletişim</div>
          <div className="flex gap-3">
            <div className="relative" ref={wrapperRef}>
              <button
                type="button"
                onClick={() => setMailOpen((v) => !v)}
                data-testid="social-mail"
                aria-label="E-posta adresini göster"
                aria-expanded={mailOpen}
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-cyan-400 hover:text-black hover:bg-cyan-400 hover:border-cyan-400 transition"
              >
                <Mail className="w-5 h-5" />
              </button>
              {mailOpen && (
                <div
                  role="dialog"
                  data-testid="mail-popup"
                  className="absolute bottom-12 left-0 z-30 w-64 panel p-4 shadow-xl fade-up"
                  style={{ background: "rgba(18,20,28,0.98)" }}
                >
                  <div className="mono-caps mb-1">E-posta</div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-white text-sm truncate" data-testid="mail-popup-email">{EMAIL}</span>
                    <button
                      type="button"
                      onClick={copyEmail}
                      data-testid="mail-copy-btn"
                      aria-label="E-postayı kopyala"
                      className="ml-auto w-8 h-8 rounded-full flex items-center justify-center border border-white/10 text-cyan-300 hover:text-black hover:bg-cyan-400 hover:border-cyan-400 transition"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">{copied ? "Kopyalandı" : "Kopyalamak için ikona tıkla"}</div>
                </div>
              )}
            </div>
            <a
              href="https://instagram.com/zafertufan"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-instagram"
              aria-label="Instagram"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-pink-400 hover:text-white hover:border-transparent transition"
              style={{ backgroundImage: "linear-gradient(135deg, rgba(255,45,146,0), rgba(255,116,55,0))" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundImage = "linear-gradient(135deg, #833AB4, #E1306C 55%, #FCAF45)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundImage = "linear-gradient(135deg, rgba(255,45,146,0), rgba(255,116,55,0))"; }}
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://youtube.com/@zafertufann"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-youtube"
              aria-label="YouTube"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-red-500 hover:text-white hover:bg-red-600 hover:border-red-600 transition"
            >
              <Youtube className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mt-8 text-xs text-slate-500 font-mono uppercase tracking-widest">
        © {new Date().getFullYear()} zafertufan.com · Studio Session Live
      </div>
    </footer>
  );
}
